import type { ReactNode } from 'react';
import React from 'react';
import { Button, useStyles, pxToRem } from '@archipro-design/aria';
import * as S from './Details.style';
import { DownloadLine, EditorLink } from '@archipro-design/icons';
import { getDetailsItems } from './util/get-details-items';
import {
    DOWNLOAD_ATTR_FILE_ID,
    DOWNLOAD_ATTR_FILE_TITLE,
    DOWNLOAD_ATTR_PROFESSIONAL_ID,
    DOWNLOAD_ATTR_PROFESSIONAL_TITLE,
    DOWNLOAD_ATTR_PROJECT_ID,
    TRACK_DOWNLOAD_ATTR,
} from '@archipro-website/tracker';
import type { ProjectPageLoader } from '@modules/project/type';
import { useFeature } from '~/modules/root';
import { SectionNotAvailable } from '../details-accordion/SectionNotAvilable';

interface DetailsTableProps {
    data: ProjectPageLoader['project'];
    openEnquiryModal: () => void;
}

const MAX_LIMITED_LINES = 8;

export const DetailsTable = ({ data, openEnquiryModal }: DetailsTableProps) => {
    const { css } = useStyles();
    const hasProjectAddressFeature = useFeature('feature_project_address');
    const [showingAll, setShowingAll] = React.useState(false);

    let index = 1; // Start at 1 to cater for the first professional line

    const detailsItems = getDetailsItems(data, {
        includeAddress: hasProjectAddressFeature,
        includeDownloads: false,
    });

    const showItem = (
        label: string,
        value: string,
        link?: string,
        isExternal?: boolean
    ): ReactNode => {
        if (!showingAll && ++index > MAX_LIMITED_LINES) {
            return null;
        }
        const final = value.replace(/(m)([23])/i, '$1<sup>$2</sup>');
        return (
            <React.Fragment key={`${label}_${value}`}>
                <span className={'text-lg text-[#848484]'}>{`${label} `}</span>
                {link ? (
                    <a
                        className="text-lg"
                        href={link}
                        download={label && !isExternal}
                        target={isExternal ? '_blank' : undefined}
                        {...{
                            [TRACK_DOWNLOAD_ATTR]: '1',
                            [DOWNLOAD_ATTR_PROFESSIONAL_TITLE]:
                                data.Professional.Title,
                            [DOWNLOAD_ATTR_FILE_TITLE]: value,
                            [DOWNLOAD_ATTR_FILE_ID]: data.ID,
                            [DOWNLOAD_ATTR_PROFESSIONAL_ID]:
                                data.Professional.ID,
                            [DOWNLOAD_ATTR_PROJECT_ID]: data.ID,
                        }}
                        rel="noopener"
                    >
                        <div>
                            {isExternal ? (
                                <EditorLink
                                    variables={{
                                        '1xSize': pxToRem(18),
                                    }}
                                />
                            ) : (
                                <DownloadLine />
                            )}
                        </div>
                        <div dangerouslySetInnerHTML={{ __html: final }} />
                    </a>
                ) : (
                    <div
                        className={'text-lg'}
                        dangerouslySetInnerHTML={{ __html: final }}
                    />
                )}
            </React.Fragment>
        );
    };

    if (detailsItems.length === 0) {
        return (
            <SectionNotAvailable
                description={
                    <>
                        No project details available for this project.
                        <br />
                        Request more information from this professional.
                    </>
                }
                enquiryButtonText="Request Project Details"
                openEnquiryModal={openEnquiryModal}
            />
        );
    }

    return (
        <div className={css(S.DetailsContainer)}>
            {detailsItems.length ? (
                <>
                    <div className="flex flex-col gap-1">
                        {detailsItems.map((item, itemIndex) => {
                            if (Array.isArray(item.value)) {
                                return item.value.map((val, i) => {
                                    const content = showItem(
                                        i === 0 ? item.itemName : '',
                                        val.Title,
                                        val.InternalLink || val.ExternalLink,
                                        !!val.ExternalLink
                                    );
                                    return content ? (
                                        <div
                                            key={`${item.itemName}-${i}`}
                                            className="grid grid-cols-[19.8125rem_1fr] gap-4"
                                        >
                                            {content}
                                        </div>
                                    ) : null;
                                });
                            }

                            const content = showItem(
                                item.itemName,
                                item.value,
                                item.link,
                                item.isExternal
                            );
                            return content ? (
                                <div
                                    key={`${item.itemName}-${itemIndex}`}
                                    className="grid grid-cols-[19.8125rem_1fr] gap-4"
                                >
                                    {content}
                                </div>
                            ) : null;
                        })}
                    </div>
                    {detailsItems.length > MAX_LIMITED_LINES - 1 && (
                        <div className="grid grid-cols-[57.75rem_23rem_1fr]">
                            <div />
                            <Button
                                onClick={() => setShowingAll(!showingAll)}
                                size={16}
                                transparent
                                variables={{
                                    buttonPadding: 0,
                                }}
                                className={css(
                                    showingAll
                                        ? S.ShowLessButton
                                        : S.ShowMoreButton
                                )}
                            >
                                {`Show ${
                                    showingAll ? 'less' : 'more'
                                } project details`}
                            </Button>
                        </div>
                    )}
                </>
            ) : null}
        </div>
    );
};
